import { useState, useEffect } from 'react';
import { get, post, del } from '../api';
import SearchableSelect from './SearchableSelect';

const C = {
  bg: '#0a0702', card: '#1a1208', line: '#3a2a12', cream: '#f5e6c8',
  gold: '#c9a96e', green: '#10b981', red: '#e63c3c', amber: '#f59e0b',
  dim: 'rgba(245,230,200,0.6)'
};

const DOC_TYPES = ['I-9', 'Resume', 'Waiver', 'Availability', 'Food Handler', 'RBS', 'Other'];

function confColor(c) {
  if (c === 'high') return C.green;
  if (c === 'medium') return C.amber;
  if (c === 'manual') return C.gold;
  return C.dim;
}

// Red, non-blocking banner shown when a scan matches someone previously
// declined/fired/DNR'd. Informational only — the scan still files normally.
const reapplicantBanner = {
  background: 'rgba(230,60,60,0.15)', border: '1px solid ' + C.red, color: C.red,
  borderRadius: 8, padding: '6px 10px', marginBottom: 10, fontSize: 13, fontWeight: 600
};

export function ScanInboxView() {
  const [scans, setScans] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ file_name: '', doc_type: 'I-9', detected_name: '', detected_text: '' });
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const s = await get('/scan/intake');
      const c = await get('/hiring/candidates');
      setScans(Array.isArray(s) ? s : []);
      setCandidates(Array.isArray(c) ? c : []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }
  useEffect(function () { load(); }, []);

  async function addScan() {
    if (!form.file_name) return;
    setBusy(true);
    try {
      await post('/scan/intake', form);
      setForm({ file_name: '', doc_type: 'I-9', detected_name: '', detected_text: '' });
      await load();
    } catch (e) { /* ignore */ }
    setBusy(false);
  }

  async function assign(id) {
    const cid = picks[id];
    if (!cid) return;
    setBusy(true);
    try { await post('/scan/intake/' + id + '/assign', { candidate_id: cid }); await load(); } catch (e) { /* ignore */ }
    setBusy(false);
  }

  async function discard(id) {
    setBusy(true);
    try { await del('/scan/intake/' + id); await load(); } catch (e) { /* ignore */ }
    setBusy(false);
  }

  const waiting = scans.filter(function (s) { return s.status === 'waiting'; });
  const filed = scans.filter(function (s) { return s.status === 'filed'; });

  const inputStyle = { background: C.bg, border: '1px solid ' + C.line, color: C.cream, borderRadius: 8, padding: '8px 10px', fontSize: 14, width: '100%', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', color: C.dim, marginBottom: 4, display: 'block' };

  return (
    <div style={{ maxWidth: 920 }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", color: C.cream, fontSize: 28, margin: '0 0 4px' }}>Scan Inbox</h1>
      <p style={{ color: C.dim, marginTop: 0 }}>Scanned docs (I-9, resume, waiver, availability) get read, matched to a person, and filed automatically. Anything it can't match confidently waits here for you to assign.</p>

      <div style={{ background: C.card, border: '1px solid ' + C.line, borderRadius: 12, padding: 16, marginBottom: 24 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: C.gold, marginBottom: 10 }}>Run an intake manually (test)</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>File name</label><input style={inputStyle} value={form.file_name} onChange={function (e) { setForm(Object.assign({}, form, { file_name: e.target.value })); }} placeholder="scan_001.pdf" /></div>
          <div><label style={labelStyle}>Doc type</label><select style={inputStyle} value={form.doc_type} onChange={function (e) { setForm(Object.assign({}, form, { doc_type: e.target.value })); }}>{DOC_TYPES.map(function (d) { return (<option key={d} value={d}>{d}</option>); })}</select></div>
          <div><label style={labelStyle}>Detected name</label><input style={inputStyle} value={form.detected_name} onChange={function (e) { setForm(Object.assign({}, form, { detected_name: e.target.value })); }} placeholder="Caleb Folkes" /></div>
          <div><label style={labelStyle}>Detected text (availability, etc.)</label><input style={inputStyle} value={form.detected_text} onChange={function (e) { setForm(Object.assign({}, form, { detected_text: e.target.value })); }} placeholder="optional" /></div>
        </div>
        <button onClick={addScan} disabled={busy || !form.file_name} style={{ marginTop: 12, background: C.gold, color: '#1a1208', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, cursor: 'pointer' }}>Run intake</button>
      </div>

      {loading ? (<p style={{ color: C.dim }}>Loading...</p>) : (
        <div>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: C.amber, marginBottom: 10 }}>Waiting to Designate ({waiting.length})</div>
          {waiting.length === 0 ? (<p style={{ color: C.dim, marginTop: 0 }}>Nothing waiting — everything matched.</p>) : waiting.map(function (s) {
            return (
              <div key={s.id} style={{ background: C.card, border: '1px solid ' + C.amber, borderRadius: 12, padding: 14, marginBottom: 10 }}>
                {s.flag ? (<div style={reapplicantBanner}>⚠ {s.flag}</div>) : null}
                <div style={{ color: C.cream, fontWeight: 600 }}>{s.file_name} <span style={{ color: C.dim, fontWeight: 400, fontSize: 13 }}>· {s.doc_type}</span></div>
                <div style={{ color: C.dim, fontSize: 13, margin: '4px 0 10px' }}>Read name: "{s.detected_name || '—'}"{s.detected_text ? ' · ' + s.detected_text : ''}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <SearchableSelect
                    style={{ flex: 1 }}
                    value={picks[s.id] || ''}
                    onChange={function (v) { setPicks(Object.assign({}, picks, { [s.id]: v })); }}
                    options={candidates.map(function (c) { return { value: c.id, label: c.name }; })}
                    placeholder="Choose person…"
                  />
                  <button onClick={function () { assign(s.id); }} disabled={busy || !picks[s.id]} style={{ background: C.green, color: '#06281d', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}>File it</button>
                  <button onClick={function () { discard(s.id); }} disabled={busy} style={{ background: 'transparent', color: C.red, border: '1px solid ' + C.red, borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}>Discard</button>
                </div>
              </div>
            );
          })}

          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: C.green, margin: '24px 0 10px' }}>Filed ({filed.length})</div>
          {filed.length === 0 ? (<p style={{ color: C.dim, marginTop: 0 }}>No scans filed yet.</p>) : filed.map(function (s) {
            return (
              <div key={s.id} style={{ background: C.card, border: '1px solid ' + (s.flag ? C.red : C.line), borderRadius: 12, padding: '12px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: C.cream }}>{s.file_name} <span style={{ color: C.dim, fontSize: 13 }}>· {s.doc_type}</span></div>
                  <div style={{ color: C.dim, fontSize: 13 }}>→ {s.candidate_name || '(unknown)'}</div>
                  {s.flag ? (<div style={Object.assign({}, reapplicantBanner, { marginTop: 6, marginBottom: 0 })}>⚠ {s.flag}</div>) : null}
                </div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: confColor(s.confidence), border: '1px solid ' + confColor(s.confidence), borderRadius: 20, padding: '3px 10px' }}>{s.confidence || 'filed'}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
